import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { RotateCcw, SlidersHorizontal, X } from 'lucide-react';

import GenericSelect from '../../../components/ui/GenericSelect';
import Button from '../../../components/ui/Button';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { nameLookup } from '../../../components/performance/PmAnalyticsFilterBar';
import { Field } from '../../performance-me/styles';
import { stageLabel } from '../../performance-me/config';
import {
  DUES, STAGE_ORDER, TASK_STATUSES, activeAdvancedKeys,
} from '../config';

// PM Analitik Faz 2 (spec §9.1) — E17 üst şeridi: Dönem, Ekip ("Tüm Alt Ekibim" yalnız SkipLevelVisibilityEnabled açıkken) ve
// Ekip sekmesinde Son Tarih (Gecikmiş / Bu Hafta / Tümü) + Gelişmiş (ünvan, aşama, görev durumu; GenericFormDrawer). Durum URL'de.
const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const ChipRemove = styled(Button)`
  min-height: 0;
  padding: 0;
  line-height: 0;
`;

export default function TeamFilterBar({ filters, onChange, periods, skipLevelEnabled, showTaskFilters }) {
  const { t } = useTranslation();
  const [drawerInitial, setDrawerInitial] = useState(null);
  const titles = useMemo(() => nameLookup('/v1/titles'), []);

  const periodOptions = (periods || []).map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  }));
  const teamOptions = [
    { id: 'direct', name: t('performance.workspace.team.filter.direct') },
    ...(skipLevelEnabled ? [{ id: 'subtree', name: t('performance.workspace.team.filter.subtree') }] : []),
  ];
  const dueOptions = DUES.map((d) => ({ id: d, name: t(`performance.workspace.team.filter.due.${d}`) }));

  const schema = [
    { name: 'titleIds', label: t('performance.workspace.team.filter.titles'), type: 'asyncSelect', isMulti: true, grid: 12, ...titles },
    {
      name: 'stage', label: t('performance.workspace.team.filter.stage'), type: 'select', grid: 12,
      options: STAGE_ORDER.map((s) => ({ id: s, name: stageLabel(t, s) })), idKey: 'id', labelKey: 'name',
    },
    {
      name: 'taskStatuses', label: t('performance.workspace.team.filter.taskStatus'), type: 'asyncSelect', isMulti: true, grid: 12, minChars: 0,
      onSearch: async () => TASK_STATUSES.map((s) => ({ id: s, name: t(`performance.operations.taskStatus.${s}`) })),
      onGetById: async (id) => ({ id, name: t(`performance.operations.taskStatus.${id}`) }),
    },
  ];

  const active = activeAdvancedKeys(filters);
  const chipLabel = (key) => {
    if (key === 'stage') return `${t('performance.workspace.team.filter.stage')}: ${stageLabel(t, filters.stage)}`;
    const count = (filters[key] || []).length;
    return `${t(`performance.workspace.team.filter.chip.${key}`)} (${count})`;
  };
  const clear = (key) => onChange({ ...filters, [key]: key === 'stage' ? null : [] });

  return (
    <>
      <Bar data-filters="team">
        <Field $w="280px">
          <GenericSelect
            label={t('performance.workspace.team.filter.period')}
            value={filters.periodId}
            onChange={(v) => { if (v) onChange({ ...filters, periodId: v }); }}
            options={periodOptions}
            idKey="id"
            labelKey="name"
            clearable={false}
          />
        </Field>
        <Field>
          <GenericSelect
            label={t('performance.workspace.team.filter.team')}
            value={filters.team}
            onChange={(v) => onChange({ ...filters, team: v || 'direct' })}
            options={teamOptions}
            idKey="id"
            labelKey="name"
            clearable={false}
          />
        </Field>
        {showTaskFilters ? (
          <>
            <Field>
              <GenericSelect
                label={t('performance.workspace.team.filter.dueLabel')}
                value={filters.due}
                onChange={(v) => onChange({ ...filters, due: v || 'all' })}
                options={dueOptions}
                idKey="id"
                labelKey="name"
                clearable={false}
              />
            </Field>
            <Button
              variant="outline"
              data-action="advanced"
              onClick={() => setDrawerInitial({ titleIds: filters.titleIds, stage: filters.stage || '', taskStatuses: filters.taskStatuses })}
            >
              <SlidersHorizontal size={16} aria-hidden="true" />
              {' '}
              {t('performance.workspace.team.filter.advanced')}
            </Button>
          </>
        ) : null}
      </Bar>
      {showTaskFilters && active.length > 0 ? (
        <Chips>
          {active.map((key) => (
            <Tag key={key} data-chip={key}>
              {chipLabel(key)}
              <ChipRemove variant="ghost" aria-label={t('performance.workspace.team.filter.remove')} onClick={() => clear(key)}>
                <X size={12} />
              </ChipRemove>
            </Tag>
          ))}
          <Button variant="ghost" data-action="reset" onClick={() => onChange({ ...filters, titleIds: [], stage: null, taskStatuses: [] })}>
            <RotateCcw size={14} aria-hidden="true" />
            {' '}
            {t('performance.workspace.team.filter.reset')}
          </Button>
        </Chips>
      ) : null}
      <GenericFormDrawer
        open={drawerInitial !== null}
        onClose={() => setDrawerInitial(null)}
        title={t('performance.workspace.team.filter.advanced')}
        schema={schema}
        initialValues={drawerInitial || undefined}
        submitLabel={t('performance.workspace.team.filter.apply')}
        onSave={async (values) => {
          onChange({
            ...filters,
            titleIds: values.titleIds || [],
            stage: values.stage || null,
            taskStatuses: values.taskStatuses || [],
          });
          return true;
        }}
      />
    </>
  );
}
